import { useState } from 'react';
import useSWR from 'swr';
import { useParams } from 'react-router-dom';

import { invariant } from '@utils/invariant';
import { ITableHeader, Table } from 'components/table/Table';
import { Button } from 'components/button/Button';
import { SpinnerBlock } from '../../../../components/Spinner';
import { EmptyState } from 'components/empty-state/EmptyState';
import { fetchAdminApiLogs } from './customer-api-key-models';
import { ErrorState } from 'components/error-state/ErrorState';

function getStatusColor(statusCode: number): string {
  if (statusCode >= 200 && statusCode < 300) return 'text-positive-text';
  if (statusCode >= 400 && statusCode < 500) return 'text-warning-text';
  return 'text-negative-text';
}

const tableHeaders: ITableHeader[] = [
  { id: 'time', name: 'Tijdstip' },
  { id: 'apiKey', name: 'API Sleutel' },
  { id: 'method', name: 'Methode' },
  { id: 'path', name: 'Pad' },
  { id: 'status', name: 'Status' },
  { id: 'ip', name: 'IP' },
  { id: 'responseTime', name: 'Responstijd' },
];

export const CustomerApiLogsPage = () => {
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  invariant(customerIdParam);
  const customerId = +customerIdParam;
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, error, isLoading, mutate } = useSWR(`admin-api-logs-${customerId}-${cursor ?? 'start'}`, () =>
    fetchAdminApiLogs({ customerId, cursor, limit: 50 }),
  );

  if (isLoading) {
    return <SpinnerBlock message="Logs laden..." />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => mutate()} variant="section" />;
  }

  if (!data || data.items.length === 0) {
    return <EmptyState title="Geen API logs gevonden" />;
  }

  return (
    <div>
      <Table
        idKey="id"
        headers={tableHeaders}
        data={data.items}
        mapData={(log) => [
          <span className="text-xs whitespace-nowrap" key="time">
            {new Date(log.requestedAt).toLocaleString()}
          </span>,
          <span className="text-xs" key="key">
            {log.apiKeyName}
          </span>,
          <code className="text-xs font-medium" key="method">
            {log.method}
          </code>,
          <code className="text-xs" key="path">
            {log.path}
          </code>,
          <span className={`text-xs font-medium ${getStatusColor(log.statusCode)}`} key="status">
            {log.statusCode}
          </span>,
          <span className="text-xs" key="ip">
            {log.sourceIp}
          </span>,
          <span className="text-xs" key="rt">
            {log.responseTimeMs}ms
          </span>,
        ]}
      />

      <div className="flex justify-between items-center mt-4">
        <Button color="secondary" isDisabled={!cursor} onTrigger={() => setCursor(undefined)}>
          Eerste pagina
        </Button>
        <Button
          color="secondary"
          isDisabled={!data.hasMore}
          onTrigger={() => {
            if (data.nextCursor) {
              setCursor(data.nextCursor);
            }
          }}
        >
          Volgende pagina
        </Button>
      </div>
    </div>
  );
};
